<template>
    <div class="email-with-confirm">
        <template v-if="!isCodeBlockShown">
            <ModelString
                class="model-input__main-input"
                type="email"
                :label="label"
                :value="value"
                @change="updateInnerValue"
            />

            <RsvButton
                v-if="!isInnerValueConfirmed"
                reset
                :disabled="!isSendAvailable"
                slot="button"
                :text="isMobile ? 'Отправить код' : 'Отправить код подтверждения'"
                class="model-input__sms-check-button"
                @click="requestNewCodeHandler"
            />
            <span v-if="isInnerValueConfirmed" slot="phone-confirmed" class="model-input__phone-confirmed">
                Подтвержден
            </span>

            <p
                v-if="!isInnerValueConfirmed"
                slot="need-confirm-message"
                class="model-input__need-confirm-message confirm-warning"
            >
                <template v-if="countdownValue > 0 || errorMessage">
                    <template v-if="errorMessage"> {{ errorMessage }}. </template>
                    <template v-if="countdownValue">
                        Отправка будет доступна через {{ countdownValue | countdownTimer }}
                    </template>
                </template>
                <template v-else-if="isInnerValueValid"></template>
                <template v-else-if="innerValue">Проверьте введенную электронную почту, некорректный ввод</template>
                <template v-else><br /></template>
            </p>
        </template>

        <CodeCheckBlock
            v-if="isCodeBlockShown"
            :errorMessage="errorMessage"
            :value="currentValue"
            :nextCodeRequestAt="nextCodeRequestAt"
            :secondsUntilNextCodeRequestAvailable="codeBlockCountDown"
            @nextCodeRequest="requestNewCodeHandler"
            @submit="submitForm"
            @cancel="resetForm"
            :disabled="secondsUntil['nextSubmitAt'] > 0"
        >
            <template v-slot:title> Код подтверждения отправлен на почту</template>
            <template v-slot:helper>
                Проверьте правильность написания <br />
                адреса электронной почты
            </template>
        </CodeCheckBlock>
    </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters, mapState } from 'vuex';

import CodeCheckBlock from '../UI/CodeCheckBlock/CodeCheckBlock';
import ModelString from './models/ModelString';
import RsvButton from '../UI/RsvButton/RsvButton';

const confirmState = [
    'errorMessage',
    'nextCodeRequestAt',
    'newConfirmationAvaliableAt',
    'nextSubmitAt',
    'currentValue',
    'confirmedValue',
    'isCodeBlockShown',
];

const confirmActions = [
    'loadConfirmation',
    'requestNewCode',
    'applyConfirmationCode',
    'setConfirmedValue',
    'cancelConfirmation',
    'resetErrorMessage',
];

const confirmGetters = ['checkIsConfirmed'];

const getters = ['isMobile', 'userProfileInfo'];
export default {
    name: 'EmailWithConfirm',
    props: {
        isConfirmed: Boolean,
        value: String,
        label: String,
    },
    data: () => ({
        timer: null,
        innerValue: '',
        secondsUntil: {
            newConfirmationAvaliableAt: 0,
            nextCodeRequestAt: 0,
            nextSubmitAt: 0,
        },
    }),
    methods: {
        ...mapActions('emailConfirmation', confirmActions),
        startTimer() {
            this.stopTimer();
            this.timer = window.setInterval(() => {
                this.calcSecondsUntil('newConfirmationAvaliableAt');
                this.calcSecondsUntil('nextCodeRequestAt');
                this.calcSecondsUntil('nextSubmitAt');
            }, 1000);
        },
        stopTimer() {
            if (this.timer) {
                window.clearInterval(this.timer);
            }
        },
        calcSecondsUntil(key) {
            if (!this[key]) return;
            this.$set(this.secondsUntil, key, Math.max(0, this[key].diff(moment(), 'seconds')));
        },
        async submitForm($event) {
            await this.applyConfirmationCode($event);
            this.updateInnerValue(this.currentValue);
        },
        resetForm() {
            this.cancelConfirmation();
            this.innerValue = this.value;
        },
        updateInnerValue(v) {
            this.innerValue = v;
            this.$emit('change', {
                value: this.innerValue,
                isConfirmed: this.isInnerValueConfirmed,
            });
        },
        requestNewCodeHandler() {
            this.requestNewCode(this.innerValue);
        },
    },
    computed: {
        ...mapState('emailConfirmation', confirmState),
        ...mapGetters('emailConfirmation', confirmGetters),
        ...mapGetters(getters),
        isInnerValueValid() {
            return this.innerValue && this.innerValue.length === this.innerValue.trim().length;
        },
        isInnerValueConfirmed() {
            let res = this.checkIsConfirmed(this.innerValue);
            return res;
        },
        isSendAvailable() {
            return this.isInnerValueValid && !this.countdownValue;
        },
        codeBlockCountDown() {
            return Math.max(...Object.values(this.secondsUntil));
        },
        countdownValue() {
            return Math.max(
                this.secondsUntil['newConfirmationAvaliableAt'],
                this.currentValue === this.innerValue ? this.secondsUntil['nextCodeRequestAt'] : 0
            );
        },
    },
    watch: {
        innerValue() {
            this.resetErrorMessage();
        },
    },
    components: {
        CodeCheckBlock,
        RsvButton,
        ModelString,
    },
    created() {
        this.loadConfirmation();
    },
    mounted() {
        if (this.value) {
            this.innerValue = this.value;
            if (this.isConfirmed) {
                this.setConfirmedValue(this.value);
            }
        }
        if (this.userProfileInfo['isEmailConfirmed']) {
            this.setConfirmedValue(this.userProfileInfo['email']);
        }
        this.startTimer();
    },
    destroyed() {
        this.stopTimer();
    },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/rem';

.email-with-confirm {
    position: relative;
    margin-bottom: 0;

    @include on-desktop {
        margin-bottom: 0;
    }

    .model-input__phone-confirmed {
        position: absolute;
        font-weight: 600;
        background: #c2edc2;
        border: none;
        top: mrem(23);
        right: mrem(5);
        color: #2a2626;
        width: mrem(142);
        height: mrem(32);
        font-size: mrem(12);
        line-height: mrem(24);
        padding: mrem(4) mrem(16) mrem(4) mrem(36);
        border-radius: mrem(40);

        &::before {
            content: '';
            position: absolute;
            width: mrem(16);
            height: mrem(16);
            top: mrem(7);
            left: mrem(17);
            background: url('../../assets/images/complete.svg') no-repeat;
            background-size: mrem(16) mrem(16);

            @include on-desktop {
                background-size: rem(16) rem(16);
                width: rem(16);
                height: rem(16);
                top: rem(7);
                left: rem(17);
            }
        }

        @include on-desktop {
            top: rem(23);
            right: rem(5);
            width: rem(142);
            height: rem(32);
            font-size: rem(12);
            line-height: rem(24);
            padding: rem(4) rem(16) rem(4) rem(36);
            border-radius: rem(40);
        }
    }

    .model-input__sms-check-button {
        z-index: 1;
        position: absolute;
        top: mrem(23);
        right: mrem(5);
        font-family: 'Montserrat';
        font-style: normal;
        font-weight: 600;
        text-align: center;
        color: #2a2626;
        background: #e2e2e2;
        font-size: mrem(12) !important;
        line-height: mrem(24);
        border-radius: mrem(40);
        height: mrem(32);
        border: mrem(1) solid #e2e2e2;
        width: mrem(140);

        @include on-desktop {
            top: rem(23);
            right: rem(5);
            font-size: rem(12) !important;
            line-height: rem(24);
            border-radius: rem(40);
            height: rem(32);
            border: rem(1) solid #e2e2e2;
            width: rem(238);
        }
    }

    .confirm-warning {
        position: relative;
        font-style: normal;
        font-weight: normal;
        color: #4663ab;
        margin: 0;
        font-size: mrem(12);
        line-height: mrem(24);
        bottom: mrem(-7);

        @include on-desktop {
            font-size: rem(12);
            line-height: rem(24);
            width: rem(497);
            bottom: rem(15);
        }
    }
}

::v-deep .model-input__main-input {
    .mt-input__value {
        width: 60%;

        @include on-desktop {
            width: 70%;
        }
    }
}
</style>
